/**
 * T135 — ExpenseAttachmentsList.
 *
 * Displays the attachments for a given expense on the detail page.
 * Shows filename, MIME type, byte size, truncated SHA-256, and a session-
 * bound download link (no tokenized URL — cookies carry the session per
 * FR-053a).
 */
import { useExpenseAttachments, attachmentDownloadUrl } from '../../services/api/attachments';
import { GlassPanel } from '../ui';
import {
  OcrConfidenceBadge,
  classifyAttachmentConfidence,
} from '../ocr/OcrConfidenceBadge';
import { useOcrEngineState } from '../../hooks/useOcrEngineState';
import type { ExpenseAttachmentDto } from '@armard/shared';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MiB`;
}

function truncateHash(hash: string): string {
  return hash.length > 12 ? `${hash.slice(0, 12)}…` : hash;
}

function formatFormatBadge(a: ExpenseAttachmentDto): string | null {
  if (a.isErechnungXml && a.erechnungFormat) {
    switch (a.erechnungFormat) {
      case 'zugferd':
        return 'ZUGFeRD';
      case 'factur-x':
        return 'Factur-X';
      case 'xrechnung-ubl':
        return 'XRechnung UBL';
      case 'xrechnung-cii':
        return 'XRechnung CII';
    }
  }
  return null;
}

export function ExpenseAttachmentsList({ expenseId }: { expenseId: string }) {
  const { data, isLoading, error } = useExpenseAttachments(expenseId);
  const ocrEngine = useOcrEngineState();

  if (isLoading) {
    return (
      <GlassPanel
        variant="subtle"
        padding="sm"
        data-testid="attachments-list-surface"
      >
        <div className="text-sm text-white/50">Lade Anhänge…</div>
      </GlassPanel>
    );
  }
  if (error) {
    return (
      <GlassPanel
        variant="subtle"
        padding="sm"
        data-testid="attachments-list-surface"
      >
        <div className="text-sm text-red-400">
          Anhänge konnten nicht geladen werden: {(error as Error).message}
        </div>
      </GlassPanel>
    );
  }

  const items = data?.items ?? [];

  return (
    <GlassPanel
      variant="subtle"
      padding="sm"
      data-testid="attachments-list-surface"
    >
      <h2 className="text-lg text-white mb-3">Anhänge</h2>
      {items.length === 0 ? (
        <p className="text-white/40 text-sm">Keine Anhänge vorhanden.</p>
      ) : (
        <ul className="divide-y divide-white/5">
          {items.map((a) => {
            const formatBadge = formatFormatBadge(a);
            return (
              <li
                key={a.id}
                className="flex items-center justify-between gap-3 py-2"
                data-testid={`attachment-row-${a.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white/90 truncate" title={a.fileNameOriginal}>
                      {a.fileNameOriginal}
                    </span>
                    {a.isPrimary && (
                      <span className="inline-block px-1.5 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-xs">
                        Primär
                      </span>
                    )}
                    {formatBadge && (
                      <span className="inline-block px-1.5 rounded bg-blue-500/10 border border-blue-500/30 text-blue-200 text-xs">
                        {formatBadge}
                      </span>
                    )}
                    {a.pageCount != null && a.pageCount > 1 && (
                      <span
                        className="inline-block px-1.5 rounded bg-white/5 border border-white/20 text-white/80 text-xs tabular-nums"
                        data-testid={`attachment-pagecount-${a.id}`}
                        title={`Dieser Anhang enthält ${a.pageCount} Seiten — als ein Beleg geführt.`}
                      >
                        {a.pageCount} Seiten
                      </span>
                    )}
                    {a.lifecycleStatus === 'superseded' && (
                      <span className="inline-block px-1.5 rounded bg-white/5 border border-white/10 text-white/50 text-xs">
                        Ersetzt
                      </span>
                    )}
                    {/* T476/T478: OCR confidence badge (per FR-013a + FR-015).
                        Renders engine-state when OCR isn't production OR
                        the per-field confidence classification when it is. */}
                    {ocrEngine !== 'production' && !a.isErechnungXml ? (
                      <OcrConfidenceBadge
                        kind={ocrEngine === 'mocked' ? 'inactive' : 'inactive'}
                        label={
                          ocrEngine === 'mocked'
                            ? 'OCR (gemockt)'
                            : 'OCR (deaktiviert)'
                        }
                        testid={`attachment-ocr-engine-${a.id}`}
                      />
                    ) : (
                      (() => {
                        const cls = classifyAttachmentConfidence({
                          isErechnungXml: a.isErechnungXml,
                          ocrFieldConfidences:
                            (a as ExpenseAttachmentDto & {
                              ocrFieldConfidences?: Record<string, number> | null;
                            }).ocrFieldConfidences ?? null,
                        });
                        if (!cls) return null;
                        return (
                          <OcrConfidenceBadge
                            kind={cls.kind}
                            confidence={cls.confidence}
                            testid={`attachment-ocr-confidence-${a.id}`}
                          />
                        );
                      })()
                    )}
                  </div>
                  <div className="text-xs text-white/50 mt-1 flex flex-wrap gap-3">
                    <span>{a.mimeType}</span>
                    <span className="tabular-nums">{formatBytes(a.byteSize)}</span>
                    <span
                      className="font-mono"
                      title={`SHA-256: ${a.contentHashSha256}`}
                    >
                      sha256:{truncateHash(a.contentHashSha256)}
                    </span>
                  </div>
                </div>
                <a
                  href={attachmentDownloadUrl(a.id)}
                  className="shrink-0 inline-flex items-center px-3 py-1.5 rounded border border-white/10 bg-white/5 hover:bg-white/10 text-white/80 text-sm"
                  download={a.fileNameOriginal}
                  data-testid={`attachment-download-${a.id}`}
                >
                  Herunterladen
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </GlassPanel>
  );
}
